/**
 * Página de Comunicações - JurisPrazo V2
 * Interface moderna para visualizar comunicações processuais
 */

import React, { useState, useEffect } from 'react';
import {
  Mail, Search, FileText, 
  AlertCircle, Clock, CheckCircle, RefreshCw, ArrowLeft
} from 'lucide-react';

// Serviços
import ComunicacaoService from '../services/comunicacaoService';

// Hooks
import { useTabRefresh } from '../hooks/useSmartRefresh';

const ComunicacoesPage = ({ currentUser, onNavigate }) => {
  const [comunicacoes, setComunicacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroSimples, setFiltroSimples] = useState('todas'); // todas, com_prazos
  const [comunicacaoSelecionada, setComunicacaoSelecionada] = useState(null);
  
  // Estados de paginação eficiente
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // Fixo em 20 para performance

  // Configurar refresh inteligente
  const { refresh: smartRefresh } = useTabRefresh(
    async () => {
      if (currentUser?.uid && !currentUser.isDemoUserManual) {
        await loadComunicacoes();
      }
    },
    'Comunicações'
  );

  useEffect(() => {
    if (currentUser?.uid && !currentUser.isDemoUserManual) {
      loadComunicacoes();
    } else {
      loadDemoComunicacoes();
    }
  }, [currentUser]);

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroSimples, searchTerm]);

  const loadComunicacoes = async () => {
    try {
      setLoading(true);
      console.log('🔄 Carregando comunicações do Firebase...');
      const userComunicacoes = await ComunicacaoService.getUserComunicacoes(currentUser.uid);
      
      // Log de debug detalhado
      console.log('📊 Comunicações recebidas:', {
        total: userComunicacoes.length,
        primeiras3: userComunicacoes.slice(0, 3).map(com => ({
          id: com.id,
          numeroProcesso: com.numeroProcesso,
          dataPublicacao: com.dataPublicacao,
          conteudo: com.conteudo ? com.conteudo.substring(0, 100) + '...' : 'SEM CONTEÚDO',
          tribunal: com.tribunal,
          processada: com.processada,
          dados: Object.keys(com)
        }))
      });
      
      setComunicacoes(userComunicacoes);
      console.log('✅ Comunicações carregadas:', userComunicacoes.length);
    } catch (error) {
      console.error('❌ Erro ao carregar comunicações:', error);
      // Em caso de erro, exibir comunicações demo como fallback
      loadDemoComunicacoes();
    } finally {
      setLoading(false);
    }
  };


  const loadDemoComunicacoes = () => {
    const hoje = new Date();
    const comunicacoesDemo = [];
    
    for (let i = 1; i <= 26; i++) {
      const dataBase = new Date(hoje);
      dataBase.setDate(dataBase.getDate() - (i - 1));
      
      comunicacoesDemo.push({
        id: `demo${i}`,
        numeroProcesso: `00${String(i).padStart(5, '0')}-67.2023.8.22.000${i}`,
        tribunal: i % 2 === 0 ? 'TJRO' : 'TRT23',
        dataPublicacao: dataBase.toISOString().split('T')[0],
        tipoAto: ['citação', 'sentença', 'intimação', 'despacho', 'decisão'][i % 5],
        conteudo: `COMUNICAÇÃO ${i} - ${['CITAÇÃO', 'SENTENÇA', 'INTIMAÇÃO', 'DESPACHO', 'DECISÃO'][i % 5]} - Processo nº 00${String(i).padStart(5, '0')}-67.2023.8.22.000${i}. Fica V.Sa. ${['citado(a) para, no prazo de 15 (quinze) dias, contestar a presente ação', 'intimado(a) da sentença proferida nos autos', 'intimado(a) para se manifestar sobre os documentos', 'ciente do despacho proferido', 'intimado(a) da decisão interlocutória'][i % 5]}. Tribunal: ${i % 2 === 0 ? 'TJRO' : 'TRT23'}. Data: ${dataBase.toLocaleDateString('pt-BR')}.`,
        processada: i % 3 !== 0, // 2/3 processadas, 1/3 pendentes
        prazoGerado: i % 4 !== 0, // 3/4 com prazo, 1/4 sem prazo
        dataProcessamento: i % 3 !== 0 ? dataBase.toISOString() : null
      });
    }
    
    setComunicacoes(comunicacoesDemo);
    setLoading(false);
  };

  // Função para extrair e formatar dados da comunicação
  const formatarComunicacao = (comunicacao) => {
    return {
      id: comunicacao.id,
      numeroProcesso: comunicacao.numeroProcesso || comunicacao.numeroprocessocommascara || comunicacao.numeroProcessoComMascara || 'N/A',
      tribunal: comunicacao.tribunal || comunicacao.siglaTribunal || comunicacao.sigla_tribunal || 'N/A',
      dataPublicacao: comunicacao.dataPublicacao || comunicacao.data_publicacao || comunicacao.data_disponibilizacao || new Date().toISOString().split('T')[0],
      tipoAto: comunicacao.tipoAto || comunicacao.tipo_ato || comunicacao.aiAnalysis?.tipoAto || 'Comunicação',
      conteudo: comunicacao.conteudo || comunicacao.texto || comunicacao.textoCompleto || comunicacao.texto_comunicacao || 'Conteúdo não disponível para visualização.',
      processada: comunicacao.processada || !!comunicacao.aiAnalysis || false,
      prazoGerado: comunicacao.prazoGerado || (comunicacao.aiAnalysis?.temPrazo) || false,
      dataProcessamento: comunicacao.dataProcessamento || comunicacao.processadaEm || null,
      temPrazo: comunicacao.prazoGerado || (comunicacao.aiAnalysis?.temPrazo) || false
    };
  };

  const filtrarPorData = (comunicacao) => {
    if (filtroData === 'todas') return true;
    
    const dataPublicacao = new Date(comunicacao.dataPublicacao);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);
    
    switch (filtroData) {
      case 'hoje':
        return dataPublicacao.toDateString() === hoje.toDateString();
      case 'ontem':
        return dataPublicacao.toDateString() === ontem.toDateString();
      case 'esta_semana':
        const inicioSemana = new Date(hoje);
        inicioSemana.setDate(hoje.getDate() - hoje.getDay());
        return dataPublicacao >= inicioSemana;
      case 'este_mes':
        return dataPublicacao.getMonth() === hoje.getMonth() && 
               dataPublicacao.getFullYear() === hoje.getFullYear();
      default:
        return true;
    }
  };

  // Aplicar formatação antes dos filtros
  const comunicacoesFormatadas = comunicacoes.map(com => formatarComunicacao(com));

  const comunicacoesFiltradas = comunicacoesFormatadas.filter(com => {
    // Busca simples: apenas por número do processo
    const matchesSearch = !searchTerm || 
      com.numeroProcesso?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filtro simples: todas ou apenas com prazos
    const matchesFiltro = filtroSimples === 'todas' ||
      (filtroSimples === 'com_prazos' && com.temPrazo);

    return matchesSearch && matchesFiltro;
  });

  // Aplicar paginação eficiente
  const totalItems = comunicacoesFiltradas.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const comunicacoesParaExibir = comunicacoesFiltradas.slice(startIndex, endIndex);

  const getStatusBadge = (comunicacao) => {
    if (comunicacao.processada && comunicacao.prazoGerado) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-tribunal-success to-tribunal-success-light text-white shadow-lg">
          <CheckCircle size={12} className="mr-1" />
          PROCESSADA
        </span>
      );
    } else if (comunicacao.processada && !comunicacao.prazoGerado) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-tribunal-warning to-tribunal-warning-light text-white shadow-lg">
          <AlertCircle size={12} className="mr-1" />
          SEM PRAZO
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-tribunal-error to-tribunal-error-light text-white shadow-lg">
          <Clock size={12} className="mr-1" />
          PENDENTE
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Carregando comunicações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header da Página */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">📧 Comunicações Processuais</h1>
            <p className="text-slate-600 mt-1">Gerencie suas comunicações e prazos</p>
          </div>
          <button
            onClick={loadComunicacoes}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            Atualizar
          </button>
        </div>
      </div>

        {/* Controles Simplificados */}
        <section className="mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex flex-col gap-4">
              {/* Filtros Simples */}
              <div className="flex flex-wrap gap-3">
                {[
                  { key: 'todas', label: '📄 Todas as Comunicações', count: comunicacoesFormatadas.length },
                  { key: 'com_prazos', label: '⏰ Com Prazos Encontrados', count: comunicacoesFormatadas.filter(c => c.temPrazo).length }
                ].map(filtro => (
                  <button
                    key={filtro.key}
                    onClick={() => setFiltroSimples(filtro.key)}
                    className={`
                      inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition-all
                      ${filtroSimples === filtro.key
                        ? 'bg-slate-800 text-white border-slate-800 shadow-md'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400'
                      }
                    `}
                  >
                    {filtro.label}
                    <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${
                      filtroSimples === filtro.key 
                        ? 'bg-white text-slate-800' 
                        : 'bg-slate-200 text-slate-700'
                    }`}>
                      {filtro.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Busca Simples */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="🔍 Buscar por número do processo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition-all"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Mostrar total de resultados */}
                  <span className="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg font-medium">
                    📋 {comunicacoesFiltradas.length} comunicação(ões)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lista de Comunicações */}
        <section>
          <div className="bg-white/90 backdrop-blur-sm border border-blue-200 rounded-xl shadow-lg overflow-hidden">
            {/* Cabeçalho Simplificado */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
              <h2 className="text-xl font-bold text-white drop-shadow-sm">
                📧 Suas Comunicações
              </h2>
              <p className="text-blue-100 mt-1 font-medium">
                {filtroSimples === 'todas' ? 'Todas as comunicações' : 'Comunicações com prazos encontrados'}
              </p>
            </div>

            {/* Conteúdo */}
            <div className="p-6">
              {comunicacoesParaExibir.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-tribunal-primary/20 to-tribunal-primary/40 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                    <Mail size={32} className="text-tribunal-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-tribunal-text-primary mb-3">
                    Nenhuma comunicação encontrada
                  </h3>
                  <p className="text-gray-600 font-medium">
                    {filtroSimples === 'todas'
                      ? 'Não há comunicações cadastradas ainda.'
                      : 'Nenhuma comunicação com prazos encontrados.'
                    }
                  </p>
                  {!currentUser?.isDemoUserManual && (
                    <div className="mt-4 p-4 bg-tribunal-info-bg border border-tribunal-primary/30 rounded-lg max-w-md mx-auto">
                      <p className="text-sm text-tribunal-primary">
                        <strong>💡 Dica:</strong> Execute a sincronização no Dashboard para buscar comunicações dos tribunais.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-6">
                    {comunicacoesParaExibir.map((comunicacao, index) => (
                      <div
                        key={comunicacao.id || index}
                        className="bg-gradient-to-br from-tribunal-surface to-tribunal-surface-hover border border-tribunal-border rounded-xl p-6 shadow-modern-lg hover:shadow-modern-xl transition-all duration-300 transform hover:-translate-y-1"
                      >
                        {/* Header do Card */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-tribunal-primary shadow-modern">
                              <FileText size={20} className="text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-lg text-tribunal-text-primary mb-1">
                                {comunicacao.tipoAto?.charAt(0).toUpperCase() + comunicacao.tipoAto?.slice(1) || 'Comunicação'}
                              </h3>
                              <div className="flex items-center gap-4 text-sm text-tribunal-text-secondary">
                                <span className="font-mono font-semibold bg-tribunal-background px-2 py-1 rounded border border-tribunal-border">
                                  {comunicacao.numeroProcesso}
                                </span>
                                <span className="font-medium">
                                  📅 {new Date(comunicacao.dataPublicacao).toLocaleDateString('pt-BR')}
                                </span>
                                <span className="font-bold text-tribunal-text-primary">
                                  {comunicacao.tribunal}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {getStatusBadge(comunicacao)}
                        </div>

                        {/* Conteúdo - SEMPRE VISÍVEL */}
                        <div className="mb-4">
                          <div className="bg-tribunal-surface p-4 rounded-lg border border-tribunal-border">
                            <div className="font-medium text-tribunal-text-primary mb-2 flex items-center gap-1">
                              📄 Conteúdo da Comunicação:
                            </div>
                            <div className="text-tribunal-text-secondary text-sm">
                              <div className="line-clamp-4">
                                {comunicacao.conteudo || 'Conteúdo não disponível'}
                              </div>
                              {comunicacao.conteudo && comunicacao.conteudo.length > 200 && (
                                <button
                                  onClick={() => setComunicacaoSelecionada(comunicacao)}
                                  className="text-tribunal-primary hover:text-tribunal-primary-dark text-xs mt-2 font-medium"
                                >
                                  Clique para ver conteúdo completo...
                                </button>
                              )}
                            </div>
                          </div>
                        </div>


                        {/* Metadados */}
                        <div className="flex items-center justify-between text-xs text-tribunal-text-light">
                          <span>
                            ID: {comunicacao.id}
                          </span>
                          {comunicacao.dataProcessamento && (
                            <span>
                              Processada em: {new Date(comunicacao.dataProcessamento).toLocaleString('pt-BR')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                   {/* Paginação Simples */}
                   {totalPages > 1 && (
                     <div className="mt-8 pt-6 border-t border-blue-200">
                       <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                         {/* Info da paginação */}
                         <div className="text-sm text-gray-600">
                           Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} comunicações
                         </div>

                         {/* Controles de paginação */}
                         <div className="flex items-center gap-2">
                           <button
                             onClick={() => setCurrentPage(1)}
                             disabled={currentPage === 1}
                             className="px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                           >
                             ⏮️ Primeira
                           </button>
                           
                           <button
                             onClick={() => setCurrentPage(prev => prev - 1)}
                             disabled={currentPage === 1}
                             className="px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                           >
                             ⬅️ Anterior
                           </button>

                           {/* Páginas */}
                           <span className="px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg">
                             {currentPage} de {totalPages}
                           </span>

                           <button
                             onClick={() => setCurrentPage(prev => prev + 1)}
                             disabled={currentPage === totalPages}
                             className="px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                           >
                             Próxima ➡️
                           </button>
                           
                           <button
                             onClick={() => setCurrentPage(totalPages)}
                             disabled={currentPage === totalPages}
                             className="px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                           >
                             Última ⏭️
                           </button>
                         </div>
                       </div>
                     </div>
                   )}
                </>
              )}
            </div>
          </div>
        </section>

      {/* Modal de Detalhes Simplificado */}
      {comunicacaoSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">📧 Detalhes da Comunicação</h3>
              <button
                onClick={() => setComunicacaoSelecionada(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <span className="font-medium text-gray-700">Processo:</span>
                <p className="text-gray-900">{comunicacaoSelecionada.numeroProcesso}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700">Tipo:</span>
                <p className="text-gray-900">{comunicacaoSelecionada.tipoAto}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700">Data:</span>
                <p className="text-gray-900">{comunicacaoSelecionada.dataPublicacao}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700">Conteúdo:</span>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{comunicacaoSelecionada.conteudo}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                {comunicacaoSelecionada.temPrazo ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                    ✅ Prazo Encontrado
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-800">
                    📄 Sem Prazo
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComunicacoesPage;
